import React, { Fragment, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import InlineRename from "./components/InlineRename.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { getEntityAliases, putEntityAlias, deleteEntityAlias } from "./api";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

const ENTITY_TYPES = [
  { value: "project", label: "Project" },
  { value: "service", label: "Service" },
  { value: "sku_category", label: "SKU category" },
  { value: "label", label: "Label" },
  { value: "ai_workspace", label: "AI workspace" },
];

const controlCls =
  "rounded-ctrl border border-border bg-card px-2.5 py-1.5 text-[13px] text-text focus:outline-none focus:ring-1 focus:ring-primary";

const NamesRegistryPage = () => {
  const queryClient = useQueryClient();
  const [entityType, setEntityType] = useState("project");
  const [labelKey, setLabelKey] = useState("");
  const [search, setSearch] = useState("");
  const [editingKey, setEditingKey] = useState(null);

  const labelReady = entityType !== "label" || labelKey.trim() !== "";

  const { data, isLoading, error, refetch } = useQuery(
    ["entity-aliases", entityType, labelKey, search],
    () => getEntityAliases({ entityType, search, labelKey }),
    { enabled: labelReady, keepPreviousData: true }
  );

  const rows = data?.aliases || [];

  const save = async (row, alias) => {
    await putEntityAlias({ entityType, entityKey: row.entity_key, alias });
    setEditingKey(null);
    queryClient.invalidateQueries(["entity-aliases"]);
  };

  const resetAuto = async (row) => {
    await deleteEntityAlias({ entityType, entityKey: row.entity_key });
    queryClient.invalidateQueries(["entity-aliases"]);
  };

  const columns = useMemo(
    () => [
      {
        id: "name",
        accessor: "label",
        Header: () => <span>Name</span>,
        Cell: ({ row }) => {
          const r = row.original;
          return editingKey === r.entity_key ? (
            <InlineRename initial={r.label} onSave={(alias) => save(r, alias)} onCancel={() => setEditingKey(null)} />
          ) : (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-text truncate">{r.label}</span>
              <button
                type="button"
                aria-label={`Rename ${r.label}`}
                onClick={() => setEditingKey(r.entity_key)}
                className="shrink-0 text-xs text-text-3 transition-colors hover:text-text"
              >
                ✎
              </button>
            </span>
          );
        },
        flex: 1,
        minWidth: 200,
      },
      {
        id: "raw",
        accessor: "entity_key",
        Header: () => <span>Raw key</span>,
        Cell: ({ value }) => <span className="font-mono text-xs text-text-2 truncate">{value}</span>,
        width: 220,
        minWidth: 140,
        maxWidth: 320,
      },
      {
        id: "source",
        Header: () => <span>Source</span>,
        Cell: ({ row }) =>
          row.original.overridden ? (
            <span className="inline-flex items-center rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary">Manual</span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-text-3">Auto</span>
          ),
        width: 110,
        minWidth: 90,
        maxWidth: 140,
      },
      {
        id: "cost",
        accessor: "monthlyCost",
        align: "right",
        Header: () => <span>Cost/mo</span>,
        Cell: ({ value }) => <span className="tabular-nums text-text">{euro(value)}</span>,
        width: 110,
        minWidth: 90,
        maxWidth: 140,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) =>
          row.original.overridden ? (
            <button
              type="button"
              onClick={() => resetAuto(row.original)}
              className="text-[13px] font-medium text-text-3 hover:text-text-2"
            >
              Reset to auto
            </button>
          ) : null,
        width: 140,
        minWidth: 120,
        maxWidth: 170,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingKey, entityType]
  );

  const controls = (
    <>
      <select
        aria-label="Entity type"
        value={entityType}
        onChange={(e) => {
          setEntityType(e.target.value);
          setLabelKey("");
          setSearch("");
          setEditingKey(null);
        }}
        className={`${controlCls} cursor-pointer`}
      >
        {ENTITY_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      {entityType === "label" && (
        <input
          type="text"
          aria-label="Label key"
          placeholder="Label key (e.g. team)"
          value={labelKey}
          onChange={(e) => {
            setLabelKey(e.target.value);
            setEditingKey(null);
          }}
          className={controlCls}
        />
      )}
    </>
  );

  return (
    <Fragment>
      <AdministrationHeader title="Names" showPeriodSelector={false} />

      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
        <TableCard
          title="Names"
          subtitle={labelReady ? `${rows.length} ${rows.length === 1 ? "name" : "names"}` : undefined}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search…"
          controls={controls}
          error={labelReady ? error : null}
          errorMessage="Error loading entity names."
          onRetry={() => refetch()}
        >
          {!labelReady ? (
            <EmptyState
              icon={icon({ name: "tag", style: "light" })}
              title="Choose a label key"
              message="Pick a label key above to list its values."
            />
          ) : isLoading ? (
            // No in-body spinner: the global application loader (App.js ClipLoader) already
            // indicates fetching. Blank placeholder keeps the card height stable without a
            // second spinner or an empty-state flash on first load.
            <div className="min-h-[240px]" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={icon({ name: "tag", style: "light" })}
              title={search ? "No matching names" : "No names"}
              message={search ? `No results for "${search}".` : "No entities found in the last 30 days."}
              action={search ? { label: "Clear search", onClick: () => setSearch(""), variant: "secondary" } : undefined}
            />
          ) : (
            <FlexTable columns={columns} data={rows} getRowId={(r) => r.entity_key} />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default NamesRegistryPage;
